import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { doc } from "firebase/firestore";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import { db } from "../../lib/firebase";
import { useFirestoreDocOnce } from "../../hooks/useFirestoreDocOnce";
import { useUpdateSession } from "../../hooks/useSessionMutations";
import { useUnsavedChangesGuard } from "../../hooks/useUnsavedChangesGuard";
import { useSnackbar } from "../../context/SnackbarContext";
import { pickedAudienceIdsForAudience } from "../../services/audienceService";
import { AudiencePicker } from "../../components/AudiencePicker";
import { PageHeader } from "../../components/PageHeader";
import { SpeakerLinker } from "../../components/SpeakerLinker";
import { SessionForm } from "./SessionForm";
import type { Audience, Session } from "@event-planner/shared";

type TabValue = "details" | "speakers" | "attendees";

function idsKey(ids: string[]): string {
  return [...ids].sort().join("\u0000");
}

export function SessionEditPage() {
  const { eventId, sessionId } = useParams<{
    eventId: string;
    sessionId: string;
  }>();
  const [tab, setTab] = useState<TabValue>("details");

  const [detailsDirty, setDetailsDirty] = useState(false);
  const [detailsSubmitting, setDetailsSubmitting] = useState(false);
  const [visibilityDirty, setVisibilityDirty] = useState(false);
  const [visibilitySubmitting, setVisibilitySubmitting] = useState(false);
  const handleDetailsDirty = useCallback(setDetailsDirty, []);
  const handleDetailsSubmitting = useCallback(setDetailsSubmitting, []);
  const { dialog: unsavedGuardDialog } = useUnsavedChangesGuard(
    detailsDirty || visibilityDirty,
    detailsSubmitting || visibilitySubmitting,
  );

  const docRef = useMemo(
    () => (sessionId ? doc(db, "sessions", sessionId) : null),
    [sessionId],
  );
  const {
    data: session,
    loading,
    refetch: refetchSession,
  } = useFirestoreDocOnce<Session>(docRef);

  // Load the linked audience so we can resolve the picker's initial
  // value. Named audience → [audienceId]. Anonymous composition →
  // members.includedAudienceIds.
  const audienceRef = useMemo(
    () => (session?.audienceId ? doc(db, "audiences", session.audienceId) : null),
    [session?.audienceId],
  );
  const {
    data: audience,
    loading: audienceLoading,
    refetch: refetchAudience,
  } =
    useFirestoreDocOnce<Audience>(audienceRef);

  if (loading || (session && audienceLoading)) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!session) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h5" component="h1">
          Session not found
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          This session may have been deleted.
        </Typography>
      </Box>
    );
  }

  const initialPickedAudienceIds = pickedAudienceIdsForAudience(audience);

  return (
    <Box>
      {unsavedGuardDialog}
      <PageHeader title="Edit Session" />
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as TabValue)}
        sx={{ mb: 3 }}
      >
        <Tab label="Details" value="details" />
        <Tab label="Speakers" value="speakers" />
        <Tab label="Attendees" value="attendees" />
      </Tabs>

      {/* Tabs stay mounted (display:none) so unsaved Details edits survive
          tab switches. The page-level guard above covers the route exit. */}
      <Box sx={{ display: tab === "details" ? "block" : "none" }}>
        <SessionForm
          mode="edit"
          initialData={session}
          initialPickedAudienceIds={initialPickedAudienceIds}
          showVisibilitySection={false}
          onDirtyChange={handleDetailsDirty}
          onSubmittingChange={handleDetailsSubmitting}
        />
      </Box>

      {eventId && sessionId && (
        <Box
          sx={{
            display: tab === "speakers" ? "block" : "none",
            maxWidth: 800,
          }}
        >
          <SpeakerLinker eventId={eventId} sessionId={sessionId} />
        </Box>
      )}

      {eventId && (
        <Box sx={{ display: tab === "attendees" ? "block" : "none" }}>
          <SessionAttendeesTab
            eventId={eventId}
            session={session}
            initialPickedAudienceIds={initialPickedAudienceIds}
            onDirtyChange={setVisibilityDirty}
            onSubmittingChange={setVisibilitySubmitting}
            onSaved={() => {
              refetchSession();
              refetchAudience();
            }}
          />
        </Box>
      )}
    </Box>
  );
}

// ── Attendees tab ────────────────────────────────────────────────────────────

function SessionAttendeesTab({
  eventId,
  session,
  initialPickedAudienceIds,
  onDirtyChange,
  onSubmittingChange,
  onSaved,
}: {
  eventId: string;
  session: Session;
  initialPickedAudienceIds: string[];
  onDirtyChange: (dirty: boolean) => void;
  onSubmittingChange: (submitting: boolean) => void;
  onSaved: () => void;
}) {
  const { update, loading: saving } = useUpdateSession();
  const { showSuccess, showError } = useSnackbar();

  // ADR-136: pending people are real eventParticipants with status
  // 'pending'; AudiencePicker surfaces them by their personal audience
  // ID alongside claimed members. The legacy `stagedContactOptionIds`
  // derivation is gone (`contact.stagedAudienceIds[]` no longer exists
  // as a staging arm).
  const initialPickerValue = useMemo(
    () => Array.from(new Set(initialPickedAudienceIds)),
    [initialPickedAudienceIds],
  );

  const [pickedAudienceIds, setPickedAudienceIds] =
    useState<string[]>(initialPickerValue);
  const previousInitialKeyRef = useRef(idsKey(initialPickerValue));

  const initialKey = useMemo(
    () => idsKey(initialPickerValue),
    [initialPickerValue],
  );
  const pickedKey = useMemo(
    () => idsKey(pickedAudienceIds),
    [pickedAudienceIds],
  );
  const dirty = initialKey !== pickedKey;

  useEffect(() => {
    const previousInitialKey = previousInitialKeyRef.current;
    previousInitialKeyRef.current = initialKey;
    if (pickedKey === previousInitialKey) {
      setPickedAudienceIds(initialPickerValue);
    }
  }, [initialKey, initialPickerValue, pickedKey]);

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    onSubmittingChange(saving);
  }, [saving, onSubmittingChange]);

  async function handleSaveVisibility() {
    try {
      await update(session.id, eventId, { pickedAudienceIds });
      onDirtyChange(false);
      showSuccess("Session visibility updated");
      onSaved();
    } catch (err) {
      showError(
        err instanceof Error ? err.message : "Failed to update visibility",
      );
    }
  }

  return (
    <Box sx={{ maxWidth: 800 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Visibility
      </Typography>
      {pickedAudienceIds.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No attendees will see this session until you pick at least one group
          or person.
        </Alert>
      )}
      <AudiencePicker
        eventId={eventId}
        value={pickedAudienceIds}
        onChange={setPickedAudienceIds}
        label="Visible to"
        placeholder="Add groups or people…"
        includePendingContacts
        testId="session-audience"
        required
      />

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <Button
          variant="contained"
          onClick={() => void handleSaveVisibility()}
          disabled={!dirty || saving}
        >
          Save Visibility
        </Button>
      </Box>
    </Box>
  );
}
